import { PortalType } from '@studyportals/domain-client';
import { DegreeTypeEnum } from '@studyportals/search-api-interface';

export class Degree {
	public static readonly master = new Degree('master');
	public static readonly bachelor = new Degree('bachelor');
	public static readonly phd = new Degree('phd');
	public static readonly distanceLearning = new Degree('distance-learning');
	public static readonly shortCourses = new Degree('course');
	public static readonly all: readonly Degree[] = [
		Degree.master,
		Degree.bachelor,
		Degree.phd,
		Degree.distanceLearning,
		Degree.shortCourses,
	];

	public static get values(): readonly string[] {
		return Degree.all.map((degree) => degree.value);
	}

	public readonly value: string;

	private constructor(value: string) {
		this.value = value;
	}

	public static forPortal(): Degree {
		switch (window.PortalType) {
			case PortalType.PHD:
				return Degree.phd;
			case PortalType.BACHELOR:
				return Degree.bachelor;
			case PortalType.DISTANCE_LEARNING:
				return Degree.distanceLearning;
			case PortalType.SHORT:
				return Degree.shortCourses;
			default:
				return Degree.master;
		}
	}

	public static from(value: string | null): Degree | null {
		if (value === null || !Degree.values.includes(value)) {
			return null;
		}

		return new Degree(value);
	}

	public static fromOrError(value: string | null): Degree {
		const degree = Degree.from(value);

		if (degree === null) {
			throw new Error(`Unknown degree: ${value ?? 'null'}`);
		}

		return degree;
	}

	public toEnum(): DegreeTypeEnum {
		switch (this.value) {
			case Degree.master.value:
				return DegreeTypeEnum.MASTER;
			case Degree.bachelor.value:
				return DegreeTypeEnum.BACHELOR;
			case Degree.phd.value:
				return DegreeTypeEnum.PHD;
			case Degree.distanceLearning.value:
				return DegreeTypeEnum.DISTANCE_LEARNING;
			case Degree.shortCourses.value:
				return DegreeTypeEnum.SHORT_COURSES;
			default:
				throw new Error(`Unknown degree: ${this.value}`);
		}
	}

	public toPortalName(): string {
		switch (this.value) {
			case Degree.master.value:
				return 'Mastersportal';
			case Degree.bachelor.value:
				return 'Bachelorsportal';
			case Degree.phd.value:
				return 'PhDportal';
			case Degree.distanceLearning.value:
				return 'DistanceLearningportal';
			case Degree.shortCourses.value:
				return 'ShortCoursesportal';
			default:
				throw new Error(`Unknown degree: ${this.value}`);
		}
	}

	public toProgrammeType(): string {
		switch (this.value) {
			case Degree.master.value:
				return 'Masters';
			case Degree.bachelor.value:
				return 'Bachelors';
			case Degree.phd.value:
				return 'PhDs';
			case Degree.distanceLearning.value:
				return 'Courses';
			case Degree.shortCourses.value:
				return 'Courses';
			default:
				throw new Error(`Unknown degree: ${this.value}`);
		}
	}
}
